import React, { useEffect, useState } from "react";
import { Form, Modal } from "react-bootstrap";
import { Link } from "react-router-dom";
import Button from "../command/Button/Button";
import "./informationCaptureCard.scss";
import InputForm from "../command/input/inputField";
import Select from "react-select";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { customerVerificationClient } from "../../data/verify-info";
import { addressSelectStyle } from "./addressSelectOptions";
import { useTranslation } from "react-i18next";
import ToastMessage from "../command/toast/toastMessage";
import { toast } from "react-toastify";
import { GLOBAL_CONSTANTS } from "../../utils/constants";
import { getAuthCredentials } from "../../utils/auth-utils";

interface AddBillingAddressModalProps {
  show: boolean;
  onHide: () => void;
  onContinue: any;
  shippingaddresstext?: any;
  billingaddresstext?: boolean;
  isInternational: boolean;
}

const postcodeRegex = /^[A-Za-z0-9\s]{3,10}$/;
const addressLookupDetails = yup.object().shape({
  houseNameNumber: yup.string(),
  street: yup.string(),
  county: yup.string().required("County is required"),
  country: yup.string().required("Country is required"),
  postcode: yup
    .string()
    .required("Post code is required")
    .matches(postcodeRegex, "Invalid postcode format")    
    .max(8, "Post code must be at most 8 characters"),
  addressText: yup.string(),
  town: yup.string()
});

const AddBillingAddressModal: React.FC<AddBillingAddressModalProps> = ({
  onHide,
  onContinue,
  isInternational,
  ...props
}) => {  
  // const [selectedOption, setSelectedOption] = useState<SingleValue<OptionType>>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const handleFocus = () => setIsFocused(true);  
  
  const { t } = useTranslation();

  const handleMenuOpen = () => {
    setIsMenuOpen(true);
  };
  const handleMenuClose = () => {
    setIsMenuOpen(false);
  };
  // const handleBlur = () => setIsFocused(selectedOption !== null);
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    getValues,
    watch,
    setValue,
    reset,
  } = useForm({
    //@ts-ignore
    resolver: yupResolver(addressLookupDetails),
    mode: "all",
  });
  const postcode = watch("postcode");
  const [address, setAddress] = useState<any>([]);
  const [lookupAddressData, setLookupAddressData] = useState<any>([]);
  const [checkAddress, setCheckAddress] = useState(false);
  const [displayAllAdress, setDisplayAllAdress] = useState(props.billingaddresstext ? isInternational: false);
  
  // const [showToast, setShowToast] = useState(false);
  // const [toastMessage, setToastMessage] = useState("");
  // const [toastType, setToastType] = useState("");
  
  const handleChange = (value: any) => {
    setIsFocused(true); // Ensure the label floats on change
    if (value?.line_2) {
      setValue(
        "addressText",
        `${value?.line_1}, ${value?.line_2}, ${lookupAddressData?.town}, ${lookupAddressData?.traditional_county}, ${lookupAddressData?.postcode}`
      );
      setValue("street", `${value?.line_1}, ${value?.line_2}, ${lookupAddressData?.town}`);
    } else {
      setValue(
        "addressText",
        `${value?.line_1}, ${lookupAddressData?.town}, ${lookupAddressData?.traditional_county}, ${lookupAddressData?.postcode}`
      );
      setValue("street", `${value?.line_1}, ${lookupAddressData?.town}`);
    }

    setCheckAddress(true);
    setValue("houseNameNumber", "");
    setValue("county", lookupAddressData?.traditional_county);
    setValue("country", GLOBAL_CONSTANTS.UNITEDKINGDOM);
    setValue("town", lookupAddressData.town);
  };
  const clearAddress = () => {
    setValue("street", "");
    setValue("houseNameNumber", "");
    setValue("county", "");
    setValue("country", "");
  };
  const handleAutoFill = () => {
    const post = getValues("postcode");
    customerVerificationClient
      .addressLookup(post)
      .then((data: any) => {
        if (data?.error_code) {
          toast.error("Invalid postal code");
          setAddress([]);
        } else {
          setLookupAddressData(data);
          setAddress(data.delivery_points);
          setCheckAddress(false);
          setDisplayAllAdress(false);
        }
      })
      .catch((error: any) => {
        toast.error(error?.message);
      });
  };
  const onSubmit = (values: any) => {
    const value = {
      ...values,
      addressText:
        values?.houseNameNumber && values?.street
          ? `${values?.houseNameNumber}, ${values?.street}, ${values?.postcode}`
          : !values?.houseNameNumber && values?.street
          ? `${values?.street}, ${values?.postcode}`
          : values?.houseNameNumber && !values?.street
          ? `${values?.houseNameNumber}, ${values?.postcode}`
          : !values?.houseNameNumber &&
            !values?.street &&
            `${values?.postcode}`,
    };

    onContinue(value);
    reset({
      houseNameNumber: "",
      street: "",
      county: "",
      country: "",
      postcode: "",
      addressText: "",
    });
    setDisplayAllAdress(false);
    setCheckAddress(false);
    onHide();
  };

  useEffect(() => {
    if (props.show) {
      setAddress([]);
      if (props.billingaddresstext) {
        setDisplayAllAdress(isInternational);
      }      
    }
  }, [props.show, props.billingaddresstext, isInternational]);

  return (
    <>
      <Modal
        {...props}
        aria-labelledby="billing-modal"
        centered
        onHide={onHide}
        className="billing-modal"
      >
        <div className={`${isMenuOpen ? "menu-mobile-open" : ""}`}>
          <Modal.Header closeButton>
            <Modal.Title id="billing-modal">
              {props?.billingaddresstext ? (
                t("add-billing-address")
              ) : (
                <>
                  {props?.shippingaddresstext &&
                  props?.shippingaddresstext?.addressText
                    ? t("edit-shipping-address")
                    : t("add-shipping-address")}
                </>
              )}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="billing-modal-wrapper">
              <Form className="billing-modal-form">
                <div className="postal-input-box">
                  <InputForm
                    inputId="postal-code"
                    inputLable={
                      props.billingaddresstext && isInternational
                        ? "Postcode / ZIP / Area Code"
                        : t("postal-code")
                    }
                    inputType="text"
                    required
                    inputPlaceholder={t("postal-code")}
                    register={register("postcode")}
                    error={errors?.postcode?.message}
                  />
                  {!(isInternational && props.billingaddresstext) && (
                    <Link
                      className={`${
                        !postcode || errors.postcode?.message ? "disabled" : ""
                      }  look-up-btn`}
                      onClick={handleAutoFill}
                      to={"#"}
                    >
                      {t("look-up")}
                    </Link>
                  )}
                </div>

                {!displayAllAdress ? (
                  <div>
                    {!checkAddress ? (
                      address &&
                      address.length > 0 && (
                        <>
                          <div
                            className={`floating-label-container ${
                              isFocused ? "focused" : ""
                            }`}
                          >
                            <label className="floating-label">
                              {t("address")}
                            </label>
                            <Select
                              options={address}
                              styles={addressSelectStyle(isFocused)}
                              onChange={(value: any) => handleChange(value)}
                              onFocus={handleFocus}
                              getOptionLabel={(option: any) => option.line_1}
                              getOptionValue={(option: any) => option.line_1}
                              menuPlacement="auto"
                              onMenuOpen={handleMenuOpen}
                              onMenuClose={handleMenuClose}
                            />
                          </div>
                          <Link
                            to={"#"}
                            className="site-link"
                            onClick={() => {
                              clearAddress();
                              setDisplayAllAdress(true);
                            }}
                          >
                            {t("cant-find-address")}?
                          </Link>
                        </>
                      )
                    ) : (
                      <InputForm
                        inputId="Address"
                        inputLable={t("address")}
                        register={register("addressText")}
                        inputType="text"
                        inputPlaceholder={t("address")}
                        onFocus={(value: any) => {
                          setDisplayAllAdress(true);
                        }}
                      />
                    )}
                  </div>
                ) : (
                  <div>
                    <InputForm
                      inputId="house-number"
                      inputLable={`${t("house-name")}/${t("number")}`}
                      register={register("houseNameNumber")}
                      inputType="text"
                      inputPlaceholder={`${t("house-name")}/${t("number")}`}
                    />
                    <InputForm
                      inputId="Street"
                      inputLable={t("street")}
                      register={register("street")}
                      inputType="text"
                      inputPlaceholder={t("street")}
                      // required
                    />
                    <InputForm
                      inputId="county"
                      inputLable={ props.billingaddresstext && isInternational
                        ? "County / State / Region"
                        : t("county")}
                      inputType="text"
                      register={register("county")}
                      inputPlaceholder={t("county")}
                      required
                    />
                    <InputForm
                      inputId="country"
                      inputLable={t("country")}
                      inputType="text"
                      register={register("country")}
                      inputPlaceholder={t("country")}
                      required
                    />
                  </div>
                )}
              </Form>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              btnText={t("continue")}
              disabled={displayAllAdress ? !isValid : !checkAddress}
              onClick={handleSubmit(onSubmit)}
            />
          </Modal.Footer>
        </div>
      </Modal>
      {/* <ToastMessage
      show={showToast}
      message={toastMessage}
      onClose={() => setShowToast(false)}
      type={toastType}
    /> */}
    </>
  );
};

export default AddBillingAddressModal;
